import {
  applyMove as coreApplyMove,
  createGameState as coreCreateGameState,
  initialPosition,
  legalMoves as coreLegalMoves,
  playGameMove as corePlayGameMove,
} from '../../backend/janggi-core.mjs';

export type Side = 'cho' | 'han';
export type PieceType = 'king' | 'guard' | 'chariot' | 'cannon' | 'horse' | 'elephant' | 'soldier';

export type Piece = {
  id: string;
  side: Side;
  type: PieceType;
  file: number;
  rank: number;
};

export type Square = { file: number; rank: number };
export type Move = { piece: Piece; to: Square; captured?: Piece };
export type GameState = {
  pieces: Piece[];
  sideToMove: Side;
  ruleProfile: string;
  status: 'PLAYING' | 'FINISHED';
  result: null | { reason: string; winner: Side | null; draw: boolean };
  pendingBikjang: null | { offeredBy: Side; offeredTo: Side; atPly: number };
  history: unknown[];
};

export const pieceNames: Record<PieceType, string> = {
  king: '궁',
  guard: '사',
  chariot: '차',
  cannon: '포',
  horse: '마',
  elephant: '상',
  soldier: '병졸',
};

export const pieceGlyphs: Record<Side, Record<PieceType, string>> = {
  cho: { king: '楚', guard: '士', chariot: '車', cannon: '包', horse: '馬', elephant: '象', soldier: '卒' },
  han: { king: '漢', guard: '士', chariot: '車', cannon: '包', horse: '馬', elephant: '象', soldier: '兵' },
};

export type FormationCode = 'MA_SANG_SANG_MA' | 'SANG_MA_MA_SANG' | 'MA_SANG_MA_SANG' | 'SANG_MA_SANG_MA';
export const initialPieces = (options?: { choFormation?: FormationCode; hanFormation?: FormationCode }): Piece[] => initialPosition(options) as Piece[];

export const legalMovesFor = (piece: Piece, pieces: Piece[]): Square[] => {
  return coreLegalMoves(piece.side, pieces, { allowPass: false })
    .filter((move) => move.pieceId === piece.id && move.to)
    .map((move) => move.to as Square);
};

export const applyMove = (pieces: Piece[], pieceId: string, to: Square): Piece[] => {
  const piece = pieces.find((item) => item.id === pieceId);
  if (!piece) return pieces;
  const captured = pieces.find((item) => item.id !== pieceId && item.file === to.file && item.rank === to.rank);
  return coreApplyMove(pieces, {
    pieceId,
    pieceType: piece.type,
    from: { file: piece.file, rank: piece.rank },
    to,
    capturedId: captured?.id || null,
  }) as Piece[];
};

export const createLocalGame = (options?: { choFormation?: FormationCode; hanFormation?: FormationCode }): GameState => coreCreateGameState({
  pieces: initialPieces(options),
  ruleProfile: 'KJA_TOURNAMENT_2024',
}) as GameState;

export const playLocalMove = (state: GameState, pieceId: string, to: Square): GameState => {
  const move = coreLegalMoves(state.sideToMove, state.pieces, {
    allowPass: true,
    allowPassInBikjang: Boolean(state.pendingBikjang),
  }).find((candidate) => candidate.pieceId === pieceId && candidate.to?.file === to.file && candidate.to?.rank === to.rank);
  if (!move) return state;
  const transition = corePlayGameMove(state, move);
  return transition.ok ? transition.state as GameState : state;
};

export const passLocalGame = (state: GameState): GameState => {
  const transition = corePlayGameMove(state, { isPass: true, pieceId: null, pieceType: null, from: null, to: null, capturedId: null });
  return transition.ok ? transition.state as GameState : state;
};

export const allMovesFor = (side: Side, pieces: Piece[]): Move[] => pieces
  .filter((piece) => piece.side === side)
  .flatMap((piece) => legalMovesFor(piece, pieces).map((to) => ({
    piece,
    to,
    captured: pieces.find((target) => target.file === to.file && target.rank === to.rank),
  })));

const pieceValues: Record<PieceType, number> = { king: 1000, chariot: 13, cannon: 7, horse: 5, elephant: 3, guard: 3, soldier: 2 };

export const chooseAiMove = (side: Side, pieces: Piece[], strength: 1 | 2 | 3): Move | null => {
  const moves = allMovesFor(side, pieces);
  if (!moves.length) return null;
  const noise = strength === 1 ? 8 : strength === 2 ? 3 : .8;
  return moves
    .map((move) => {
      const next = applyMove(pieces, move.piece.id, move.to);
      const material = next.reduce((score, piece) => score + (piece.side === side ? 1 : -1) * pieceValues[piece.type], 0);
      const centre = 4 - Math.abs(4 - move.to.file);
      const capture = move.captured ? pieceValues[move.captured.type] * 5 : 0;
      return { move, score: material + centre * .15 + capture + Math.random() * noise };
    })
    .sort((a, b) => b.score - a.score)[0].move;
};
